import { useState, useEffect, useRef } from 'react';
import { getMatches, uploadPDF, deleteMatch, getPDFs, changePassword } from '../api';

export default function AdminPanel() {
  const [tab, setTab]         = useState('upload');
  const [matches, setMatches] = useState([]);
  const [pdfs, setPdfs]       = useState([]);
  const [toast, setToast]     = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [m, p] = await Promise.all([getMatches(), getPDFs().catch(() => [])]);
    setMatches(m);
    setPdfs(p);
  }

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <>
      <div style={{ background: 'linear-gradient(135deg,#1a1f35,#0d1220)', borderBottom: '2px solid var(--accent)', padding: '16px 16px 0' }}>
        <div style={{ fontSize: 11, color: 'var(--accent)', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>Espace Admin</div>
        <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'Barlow Condensed', marginBottom: 12 }}>Gestion des matchs</div>
        <div className="tabs" style={{ padding: 0, background: 'transparent', borderBottom: '1px solid #1e2640' }}>
          {[['upload','📤 Upload'], ['matchs','📋 Matchs'], ['password','🔑 Mot de passe']].map(([k, l]) => (
            <button key={k} className={`tab-btn ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: '20px 16px' }}>

        {/* ── UPLOAD ── */}
        {tab === 'upload' && (
          <UploadSection onUpload={async (file) => {
            setLoading(true);
            try {
              const res = await uploadPDF(file);
              showToast(res.skipped ? 'Match déjà présent' : `✅ ${res.message}`);
              loadData();
            } catch (err) {
              showToast(`❌ ${err.message}`, 'error');
            } finally { setLoading(false); }
          }} loading={loading} pdfs={pdfs} />
        )}

        {/* ── MATCHS ── */}
        {tab === 'matchs' && (
          <MatchsList matches={matches} onDelete={async (id) => {
            if (!window.confirm('Supprimer ce match ?')) return;
            try {
              await deleteMatch(id);
              showToast('Match supprimé');
              loadData();
            } catch (err) {
              showToast(err.message, 'error');
            }
          }} />
        )}

        {/* ── MOT DE PASSE ── */}
        {tab === 'password' && (
          <ChangePassword onSave={async (current, next) => {
            try {
              await changePassword(current, next);
              showToast('✅ Mot de passe modifié');
            } catch (err) {
              showToast(err.message, 'error');
            }
          }} />
        )}
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

// ── Upload section ────────────────────────────────────────────────────────────
function UploadSection({ onUpload, loading, pdfs }) {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  function handleFiles(files) {
    if (!files?.length) return;
    Array.from(files).filter(f => f.name.endsWith('.pdf')).forEach(f => onUpload(f));
  }

  return (
    <>
      <div
        className={`upload-zone ${drag ? 'drag-over' : ''}`}
        onClick={() => inputRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
      >
        <div className="icon">📄</div>
        <p><strong>Glisse tes feuilles de match ici</strong></p>
        <p style={{ marginTop: 4, fontSize: 12 }}>ou clique pour sélectionner des PDFs</p>
        <input ref={inputRef} type="file" accept=".pdf" multiple style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
      </div>

      {loading && (
        <div style={{ textAlign: 'center', color: 'var(--accent)', padding: '16px 0', fontWeight: 700 }}>
          ⏳ Traitement en cours…
        </div>
      )}

      {pdfs.length > 0 && <ListeFeuilles pdfs={pdfs} />}
    </>
  );
}

// ── Matches list ──────────────────────────────────────────────────────────────
function MatchsList({ matches, onDelete }) {
  if (!matches.length) return (
    <div style={{ textAlign: 'center', color: '#8892a4', padding: '40px 0' }}>
      Aucun match importé.<br/>
      <span style={{ fontSize: 12 }}>Utilise l'onglet Upload pour ajouter des feuilles de match.</span>
    </div>
  );

  return matches.map(m => (
    <div className="card" key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 11, color: '#8892a4' }}>{m.date} · {m.journee} · {m.dom_ext}</div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>vs {m.adversaire}</div>
        <div style={{ fontSize: 15, marginTop: 2 }}>
          <span style={{ fontWeight: 800 }}>{m.score_nous}</span>
          <span style={{ color: '#8892a4' }}> - </span>
          <span>{m.score_eux}</span>
          <span className={`badge badge-${m.resultat}`} style={{ marginLeft: 8 }}>
            {m.resultat === 'V' ? 'V' : m.resultat === 'N' ? 'N' : 'D'}
          </span>
        </div>
      </div>
      <button className="btn btn-danger" style={{ width: 'auto', padding: '8px 14px', fontSize: 12 }} onClick={() => onDelete(m.id)}>
        🗑️ Supprimer
      </button>
    </div>
  ));
}

// ── Change password ───────────────────────────────────────────────────────────
function ChangePassword({ onSave }) {
  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr]         = useState('');

  async function handleSubmit(e) {
    e.preventDefault(); setErr('');
    if (next !== confirm) return setErr('Les mots de passe ne correspondent pas');
    if (next.length < 8)  return setErr('Minimum 8 caractères');
    try { await onSave(current, next); setCurrent(''); setNext(''); setConfirm(''); }
    catch (e) { setErr(e.message); }
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 360 }}>
      {[
        ['Mot de passe actuel', current, setCurrent, 'current-password'],
        ['Nouveau mot de passe', next, setNext, 'new-password'],
        ['Confirmer le nouveau', confirm, setConfirm, 'new-password'],
      ].map(([label, val, setter, autocomplete]) => (
        <div className="form-group" key={label}>
          <label>{label}</label>
          <input type="password" value={val} onChange={e => setter(e.target.value)} autoComplete={autocomplete} placeholder="••••••••" />
        </div>
      ))}
      {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <button type="submit" className="btn btn-primary">Changer le mot de passe</button>
    </form>
  );
}

/** Feuilles importées, regroupées par saison. */
function ListeFeuilles({ pdfs }) {
  const saisons = [...new Set(pdfs.map(f => f.saison || 'Sans saison'))];
  const taille = o => o == null ? null : o < 1024 * 1024 ? `${Math.max(1, Math.round(o / 1024))} Ko` : `${(o / 1048576).toFixed(1)} Mo`;
  return (
    <>
      <div className="section-title" style={{ marginTop: 20 }}>Feuilles importées ({pdfs.length})</div>
      {saisons.map(s => (
        <div key={s} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase', margin: '4px 0 8px' }}>
            Saison {s}</div>
          {pdfs.filter(f => (f.saison || 'Sans saison') === s).map(f => (
            <div key={f.id} style={{ background: '#111827', border: '1px solid #1e2640', borderRadius: 8, padding: '10px 14px',
                                    marginBottom: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {f.dom_ext === 'Extérieur' ? '✈️' : '🏠'} {f.adversaire || 'Adversaire inconnu'}
                  <span style={{ fontWeight: 400, color: '#8892a4' }}> · {f.date}</span></div>
                <div style={{ fontSize: 11, color: '#8892a4', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📄 {f.fichier || 'aucun fichier'}</div>
              </div>
              <span style={{ fontSize: 11, whiteSpace: 'nowrap', color: f.present ? '#8892a4' : 'var(--danger)' }}>
                {f.present ? taille(f.taille) : f.fichier ? '⚠️ fichier introuvable' : '—'}</span>
            </div>))}
        </div>))}
    </>
  );
}
